/**
 * 设备 ID 工具 - 用户识别与隐私保护
 * =====================================
 *
 * 功能：
 * - 生成唯一设备 ID
 * - 本地存储，不上传
 * - 天然用户隔离
 *
 * 隐私保护：
 * - 不收集任何个人信息
 * - 设备 ID 仅用于本地数据管理
 * - 可随时重置
 *
 * @author Claude Sonnet 4.5
 * @date 2026-02-18
 */

const DEVICE_ID_KEY = 'learn_cantonese_device_id';
const DEVICE_ID_BACKUP_KEY = 'learn_cantonese_device_id_backup';

/**
 * 生成随机设备 ID
 * @returns {string} 设备 ID（格式：xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx）
 */
function generateDeviceId() {
  // 使用 UUID v4 格式
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 获取设备 ID
 * 如果不存在则创建并保存
 * @returns {string} 设备 ID
 */
export function getDeviceId() {
  try {
    // 从 localStorage 读取
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (!deviceId) {
      // 不存在，生成新的
      deviceId = generateDeviceId();

      // 保存到 localStorage
      localStorage.setItem(DEVICE_ID_KEY, deviceId);

      // 备份（防止意外删除）
      localStorage.setItem(DEVICE_ID_BACKUP_KEY, deviceId);

      console.log('✅ 新设备 ID 已生成:', deviceId);
    } else {
      console.log('✅ 设备 ID:', deviceId);
    }

    return deviceId;
  } catch (error) {
    console.error('❌ 获取设备 ID 失败:', error);

    // 降级：生成临时 ID（每次可能不同）
    return generateDeviceId();
  }
}

/**
 * 重置设备 ID
 * ⚠️ 警告：重置后，旧数据仍保留，但新数据会使用新 ID
 *
 * 用途：
 * - 切换设备/账号
 * - 清除本地数据
 * - 隐私保护
 *
 * @returns {string} 新的设备 ID
 */
export function resetDeviceId() {
  try {
    // 清除旧 ID
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(DEVICE_ID_BACKUP_KEY);

    // 生成新 ID
    const newDeviceId = getDeviceId();

    console.log('🔄 设备 ID 已重置:', newDeviceId);

    return newDeviceId;
  } catch (error) {
    console.error('❌ 重置设备 ID 失败:', error);
    throw error;
  }
}

/**
 * 检查是否首次启动
 * @returns {boolean} 是否首次启动
 */
export function isFirstLaunch() {
  const deviceId = localStorage.getItem(DEVICE_ID_KEY);
  return !deviceId;
}

/**
 * 获取设备信息
 * @returns {Object} 设备信息对象
 */
export function getDeviceInfo() {
  return {
    deviceId: getDeviceId(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    timestamp: new Date().toISOString(),
  };
}

/**
 * 保存用户偏好设置
 * @param {string} key - 设置键
 * @param {any} value - 设置值
 */
export function saveUserPreference(key, value) {
  try {
    const prefs = getUserPreferences();
    prefs[key] = value;
    localStorage.setItem('learn_cantonese_prefs', JSON.stringify(prefs));
    console.log(`✅ 偏好设置已保存: ${key} =`, value);
  } catch (error) {
    console.error('❌ 保存偏好设置失败:', error);
  }
}

/**
 * 获取用户偏好设置
 * @param {string} key - 设置键（可选）
 * @returns {any} 设置值或所有设置
 */
export function getUserPreferences(key) {
  try {
    const prefsJson = localStorage.getItem('learn_cantonese_prefs');
    const prefs = prefsJson ? JSON.parse(prefsJson) : {};

    if (key) {
      return prefs[key];
    }

    return prefs;
  } catch (error) {
    console.error('❌ 获取偏好设置失败:', error);
    return key ? undefined : {};
  }
}

/**
 * 清除所有用户数据（危险操作）
 * ⚠️ 这将清除：
 * - 设备 ID
 * - 偏好设置
 * - 但不清除 IndexedDB 中的故事
 *
 * 如需清除故事，请使用 useIndexedDB 的 clearAll()
 */
export function clearAllUserData() {
  try {
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(DEVICE_ID_BACKUP_KEY);
    localStorage.removeItem('learn_cantonese_prefs');
    console.log('🗑️ 所有用户数据已清除');
  } catch (error) {
    console.error('❌ 清除用户数据失败:', error);
    throw error;
  }
}

/**
 * 导出用户数据（用于备份）
 * @returns {Object} 用户数据对象
 */
export function exportUserData() {
  return {
    deviceId: getDeviceId(),
    preferences: getUserPreferences(),
    deviceInfo: getDeviceInfo(),
    exportDate: new Date().toISOString(),
  };
}
